import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Shuffle, ShieldStar, FloppyDisk, ArrowCounterClockwise, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";

const ROLE_KEYS = ["super_admin", "hostel_admin", "school_admin", "teacher", "donor"];

export default function RbacAdmin() {
  const [users, setUsers] = useState([]);
  const [matrix, setMatrix] = useState({});
  const [originalMatrix, setOriginalMatrix] = useState({});
  const [permissions, setPermissions] = useState([]);
  const [schools, setSchools] = useState([]);
  const [hostels, setHostels] = useState([]);
  const [picked, setPicked] = useState(null);
  const [next, setNext] = useState({ role: "donor", school_id: "", hostel_id: "" });
  const [busy, setBusy] = useState(false);
  const [savingRole, setSavingRole] = useState(null);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");

  const refreshUsers = () => {
    const params = [];
    if (search.trim()) params.push(`q=${encodeURIComponent(search.trim())}`);
    if (roleFilter) params.push(`role=${roleFilter}`);
    const url = params.length ? `/admin/users/search?${params.join("&")}` : "/admin/users";
    return api.get(url).then((r) => setUsers(r.data || []));
  };
  const refreshMatrix = () =>
    api.get("/admin/rbac/matrix").then((r) => {
      setMatrix(r.data?.roles || {});
      setOriginalMatrix(JSON.parse(JSON.stringify(r.data?.roles || {})));
    });

  useEffect(() => {
    refreshUsers().catch(() => {});
    refreshMatrix().catch(() => {});
    api.get("/admin/rbac/permissions").then((r) => setPermissions(r.data?.permissions || [])).catch(() => {});
    api.get("/admin/schools").then((r) => setSchools(r.data || [])).catch(() => {});
    api.get("/admin/hostels").then((r) => setHostels(r.data || [])).catch(() => {});
  }, []);

  const togglePerm = (roleKey, permKey) => {
    setMatrix((m) => {
      const cur = m[roleKey] || { permissions: [] };
      const has = cur.permissions.includes(permKey);
      const next = has ? cur.permissions.filter((p) => p !== permKey) : [...cur.permissions, permKey];
      return { ...m, [roleKey]: { ...cur, permissions: next } };
    });
  };

  const isDirty = (roleKey) => {
    const a = (matrix[roleKey]?.permissions || []).slice().sort().join(",");
    const b = (originalMatrix[roleKey]?.permissions || []).slice().sort().join(",");
    return a !== b;
  };

  const saveRole = async (roleKey) => {
    setSavingRole(roleKey);
    try {
      await api.patch(`/admin/rbac/roles/${roleKey}/permissions`, {
        permissions: matrix[roleKey].permissions,
      });
      toast.success(`${matrix[roleKey].label} permissions saved`);
      await refreshMatrix();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setSavingRole(null);
    }
  };

  const revertRole = (roleKey) => {
    setMatrix((m) => ({ ...m, [roleKey]: JSON.parse(JSON.stringify(originalMatrix[roleKey])) }));
  };

  const openChange = (u) => {
    setPicked(u);
    setNext({ role: u.role, school_id: u.school_id || "", hostel_id: u.hostel_id || "" });
  };

  const saveUserRole = async () => {
    setBusy(true);
    try {
      const body = { role: next.role };
      if (next.role === "school_admin" || next.role === "teacher") body.school_id = next.school_id;
      if (next.role === "hostel_admin") body.hostel_id = next.hostel_id;
      await api.post(`/admin/rbac/users/${picked.id}/role`, body);
      toast.success(`Role updated to ${next.role.replace("_", " ")}`);
      setPicked(null);
      refreshUsers();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const roleColor = (r) => ({
    super_admin: "border-[var(--vs-secondary)]/40 text-[var(--vs-secondary)]",
    hostel_admin: "border-[var(--vs-warning)]/40 text-[var(--vs-warning)]",
    school_admin: "border-[var(--vs-success)]/40 text-[var(--vs-success)]",
    teacher: "border-blue-500/40 text-blue-700",
    donor: "border-pink-500/40 text-pink-700",
  }[r] || "");

  // Group permissions by domain (stuff before the first colon).
  const grouped = permissions.reduce((acc, p) => {
    const domain = p.key.split(":")[0];
    (acc[domain] = acc[domain] || []).push(p);
    return acc;
  }, {});

  return (
    <DashboardLayout title="Roles & permissions" subtitle="Toggle individual permissions per role. Changes apply on the next request.">
      <Tabs defaultValue="users">
        <TabsList>
          <TabsTrigger value="users" data-testid="rbac-tab-users">Users</TabsTrigger>
          <TabsTrigger value="matrix" data-testid="rbac-tab-matrix">Permission matrix</TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <div className="vs-card p-4 mb-3 flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-1 min-w-[200px]">
              <MagnifyingGlass size={14} className="text-[var(--vs-text-secondary)]" />
              <input
                data-testid="rbac-user-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && refreshUsers()}
                placeholder="Search by email or name…"
                className="flex-1 px-3 py-1.5 rounded border border-[var(--vs-border)] bg-white text-sm"
              />
            </div>
            <select data-testid="rbac-role-filter" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="px-3 py-1.5 rounded border border-[var(--vs-border)] bg-white text-sm">
              <option value="">All roles</option>
              {ROLE_KEYS.map((r) => <option key={r} value={r}>{r}</option>)}
              <option value="parent">parent</option>
            </select>
            <Button size="sm" data-testid="rbac-search-go" onClick={refreshUsers} className="bg-[var(--vs-secondary)] text-[var(--vs-bg)]">Search</Button>
          </div>
          <div className="vs-card overflow-x-auto" data-testid="rbac-users-table">
            <table className="w-full text-sm">
              <thead className="bg-[var(--vs-bg-subtle)]">
                <tr className="text-left text-[11px] uppercase font-mono tracking-widest text-[var(--vs-text-secondary)]">
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Scope</th>
                  <th className="px-4 py-3">2FA</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t border-[var(--vs-border)]">
                    <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                    <td className="px-4 py-3">{u.name}</td>
                    <td className="px-4 py-3"><Badge variant="outline" className={roleColor(u.role)}>{u.role}</Badge></td>
                    <td className="px-4 py-3 text-[11px] font-mono text-[var(--vs-text-secondary)]">
                      {u.school_id ? `school:${u.school_id.slice(0,8)}` : ""}
                      {u.hostel_id ? ` hostel:${u.hostel_id.slice(0,8)}` : ""}
                    </td>
                    <td className="px-4 py-3 text-[11px]">{u.mfa_enabled ? <ShieldStar size={14} className="text-[var(--vs-success)]" /> : <span className="text-[var(--vs-text-secondary)]">—</span>}</td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="outline" onClick={() => openChange(u)} data-testid={`rbac-change-${u.email.split("@")[0]}`}>
                        <Shuffle size={12} className="mr-1.5" /> Change role
                      </Button>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-[var(--vs-text-secondary)]">No users yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="matrix">
          <div className="space-y-4" data-testid="rbac-matrix-editable">
            <div className="text-xs text-[var(--vs-text-secondary)] vs-card p-3">
              Editable per active trust. Toggling a permission off restricts every API call wrapped in <code className="font-mono text-[10px] bg-[var(--vs-bg-subtle)] px-1 rounded">require_permission(&#39;…&#39;)</code> for users with that role. Click <kbd className="font-mono text-[10px] bg-[var(--vs-bg-subtle)] px-1 rounded">Save</kbd> to apply.
            </div>
            {ROLE_KEYS.map((roleKey) => {
              const role = matrix[roleKey];
              if (!role) return null;
              const dirty = isDirty(roleKey);
              return (
                <div key={roleKey} className="vs-card p-5" data-testid={`rbac-role-${roleKey}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className={roleColor(roleKey)}>{roleKey}</Badge>
                      <div className="font-display text-base font-semibold">{role.label}</div>
                      <span className="text-xs font-mono text-[var(--vs-text-secondary)]">{role.permissions.length} of {permissions.length} permissions</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {dirty && (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => revertRole(roleKey)} data-testid={`rbac-revert-${roleKey}`}>
                            <ArrowCounterClockwise size={12} className="mr-1.5" /> Revert
                          </Button>
                          <Button size="sm" disabled={savingRole === roleKey} onClick={() => saveRole(roleKey)} data-testid={`rbac-save-${roleKey}`} className="bg-[var(--vs-secondary)] text-[var(--vs-bg)]">
                            <FloppyDisk size={12} className="mr-1.5" /> {savingRole === roleKey ? "Saving…" : "Save"}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Object.entries(grouped).map(([domain, perms]) => (
                      <div key={domain} className="space-y-1">
                        <div className="eyebrow">{domain}</div>
                        {perms.map((p) => {
                          const checked = role.permissions.includes(p.key);
                          return (
                            <label key={p.key} className="flex items-start gap-2 cursor-pointer hover:bg-[var(--vs-bg-subtle)] px-1 py-0.5 rounded">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => togglePerm(roleKey, p.key)}
                                data-testid={`rbac-toggle-${roleKey}-${p.key}`}
                                className="mt-1 accent-[var(--vs-secondary)]"
                              />
                              <span className="text-xs">
                                <div className="font-medium">{p.label}</div>
                                <div className="font-mono text-[10px] text-[var(--vs-text-secondary)]">{p.key}</div>
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!picked} onOpenChange={(o) => !o && setPicked(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change role — {picked?.email}</DialogTitle>
            <DialogDescription>The user&#39;s existing sessions will be invalidated. Their next request re-authenticates with the new role.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <div className="text-xs eyebrow mb-1.5">New role</div>
              <Select value={next.role} onValueChange={(v) => setNext({ ...next, role: v })}>
                <SelectTrigger data-testid="rbac-new-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLE_KEYS.map((k) => <SelectItem key={k} value={k}>{k.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {(next.role === "school_admin" || next.role === "teacher") && (
              <div>
                <div className="text-xs eyebrow mb-1.5">School</div>
                <Select value={next.school_id || "none"} onValueChange={(v) => setNext({ ...next, school_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="rbac-new-school"><SelectValue placeholder="Pick a school" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">None</SelectItem>{schools.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {next.role === "hostel_admin" && (
              <div>
                <div className="text-xs eyebrow mb-1.5">Hostel</div>
                <Select value={next.hostel_id || "none"} onValueChange={(v) => setNext({ ...next, hostel_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="rbac-new-hostel"><SelectValue placeholder="Pick a hostel" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">None</SelectItem>{hostels.map((h) => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <Button onClick={saveUserRole} disabled={busy} data-testid="rbac-save" className="w-full bg-[var(--vs-secondary)] text-[var(--vs-bg)]">
              {busy ? "Saving…" : "Apply role change"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
